import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, SkipForward, Plane, Images, MessageCircleQuestion, ListChecks } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";
import { SITE } from "../lib/site";

/* TRAVEL DAY — big-button, high-contrast mobile screen for moving through the
   airport: route, gate/terminal, boarding countdown and ONE next task. */
export default function TravelDay() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [trip, setTrip] = useState(null);
  const [journey, setJourney] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const [now, setNow] = useState(Date.now());

  useEffect(() => { document.title = `Travel Day | ${SITE.name}`; }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!user) return;
    api.get(`/trips/${id}`).then((r) => setTrip(r.data)).catch(() => setTrip(null));
    api.get(`/journey-checklist/trip/${id}`).then((r) => setJourney(r.data.items[0] || null)).catch(() => {});
  }, [user, id]);

  // next departure: first upcoming flight/train segment (today or later)
  const target = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const segs = (trip?.segments || []).filter((s) => ["flight", "train", "rail"].includes(s.mode || "flight") && s.date >= today)
      .sort((a, b) => `${a.date} ${a.time || ""}`.localeCompare(`${b.date} ${b.time || ""}`));
    return segs[0] || null;
  }, [trip]);

  // details for the target: only trust a section whose flight ref matches the
  // target; otherwise show the target segment's own data (never another flight's)
  const details = useMemo(() => {
    for (const s of journey?.sections || []) {
      const d = s.details || {};
      if (d.flight && target?.ref && d.flight.toUpperCase() === target.ref.toUpperCase()) return d;
    }
    if (!target) return (journey?.sections || []).find((s) => s.kind === "origin")?.details || {};
    return {};
  }, [journey, target]);

  const boardAt = useMemo(() => {
    const dateStr = target?.date;
    const t = details.boarding_time || details.departure_time?.split(" ").pop() || target?.time;
    if (!dateStr || !t || !/^\d{2}:\d{2}/.test(t)) return null;
    return new Date(`${dateStr}T${t.slice(0, 5)}:00`);
  }, [target, details]);

  const countdown = boardAt ? boardAt.getTime() - now : null;
  const fmt = (ms) => {
    const neg = ms < 0; const s = Math.floor(Math.abs(ms) / 1000);
    const p = (n) => String(n).padStart(2, "0");
    return `${neg ? "-" : ""}${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
  };

  const tasks = useMemo(() => (journey?.sections || []).flatMap((s) =>
    s.tasks.filter((t) => !t.done && !skipped.includes(t.id)).map((t) => ({ ...t, stage: s.title }))), [journey, skipped]);
  const nextTask = tasks[0];

  const markDone = async () => {
    if (!nextTask) return;
    const { data } = await api.patch(`/journey-checklist/${journey.id}/tasks/${nextTask.id}`, { done: true });
    setJourney(data);
  };

  if (authLoading || (user && trip === null)) return <Spinner full />;
  if (!trip) return <div className="p-10 text-center text-sm text-zinc-400">Trip not found. <Link to="/travel-hub" className="text-sky-400 underline">Back to Hub</Link></div>;

  const route = (trip.stops?.length ? trip.stops : [target?.from_place, target?.to_place].filter(Boolean));

  return (
    <div className="min-h-screen bg-zinc-950 text-white px-5 py-6 max-w-2xl mx-auto" data-testid="travel-day-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to={`/trips/${trip.id}`} data-testid="travel-day-back"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> {trip.title}
        </Link>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400">Travel Day</span>
      </div>

      <div className="font-heading font-900 text-2xl sm:text-3xl leading-tight mb-4" data-testid="travel-day-route">
        {route.length ? route.join(" → ") : trip.title}
      </div>

      {target && (
        <div className="grid grid-cols-3 gap-2 mb-5 text-center" data-testid="travel-day-info">
          <div className="bg-zinc-900 border border-zinc-800 py-3">
            <div className="text-[10px] font-mono uppercase text-zinc-500">Flight / Train</div>
            <div className="font-900 text-lg">{details.flight || target.ref || "—"}</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 py-3">
            <div className="text-[10px] font-mono uppercase text-zinc-500">Terminal</div>
            <div className="font-900 text-lg">{details.terminal || target.terminal || "—"}</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 py-3">
            <div className="text-[10px] font-mono uppercase text-zinc-500">Gate / Platform</div>
            <div className="font-900 text-lg">{details.gate || target.gate || "—"}</div>
          </div>
        </div>
      )}

      {countdown !== null && (
        <div className={`border py-5 text-center mb-6 ${countdown < 45 * 60000 ? "border-rose-500 bg-rose-950/40" : "border-amber-500/50 bg-zinc-900"}`} data-testid="travel-day-countdown">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-400 mb-1">
            {details.boarding_time ? "Boarding in" : "Departure in"}
          </div>
          <div className="font-mono font-900 text-5xl tabular-nums">{fmt(countdown)}</div>
          <div className="text-xs text-zinc-400 mt-1">{target?.date} · {details.boarding_time || target?.time || ""}</div>
        </div>
      )}

      {nextTask ? (
        <div className="bg-zinc-900 border border-sky-700 p-5 mb-4" data-testid="travel-day-next-task">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-sky-400 mb-1">Next task · {nextTask.stage}</div>
          <div className="font-heading font-700 text-xl leading-snug">{nextTask.text}</div>
          {nextTask.note && <div className="text-sm text-amber-300 mt-1.5">{nextTask.note}</div>}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button onClick={markDone} data-testid="travel-day-done"
              className="py-4 bg-emerald-600 hover:bg-emerald-500 font-900 text-lg inline-flex items-center justify-center gap-2">
              <Check className="w-6 h-6" /> DONE
            </button>
            <button onClick={() => setSkipped((s) => [...s, nextTask.id])} data-testid="travel-day-skip"
              className="py-4 bg-zinc-800 hover:bg-zinc-700 font-900 text-lg inline-flex items-center justify-center gap-2">
              <SkipForward className="w-6 h-6" /> SKIP
            </button>
          </div>
          {tasks[1] && <div className="text-xs text-zinc-500 mt-3">Then: {tasks[1].text}</div>}
        </div>
      ) : (
        <div className="bg-zinc-900 border border-emerald-700 p-5 mb-4 text-center" data-testid="travel-day-all-done">
          <Plane className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
          <div className="font-heading font-700 text-lg">
            {journey ? "All checklist tasks done — safe travels!" : "No journey checklist yet."}
          </div>
          {!journey && <Link to={`/trips/${trip.id}#journey-checklist`} className="text-sky-400 text-sm underline">Start it on the trip page</Link>}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 text-center text-xs font-700">
        <Link to={`/trips/${trip.id}#journey-checklist`} data-testid="travel-day-checklist-link"
          className="py-3 bg-zinc-900 border border-zinc-800 hover:border-sky-600 inline-flex items-center justify-center gap-1.5">
          <ListChecks className="w-4 h-4" /> CHECKLIST
        </Link>
        <Link to={`/trips/${trip.id}#photos`} data-testid="travel-day-photos-link"
          className="py-3 bg-zinc-900 border border-zinc-800 hover:border-emerald-600 inline-flex items-center justify-center gap-1.5">
          <Images className="w-4 h-4" /> PHOTOS
        </Link>
        <Link to={`/trips/${trip.id}`} data-testid="travel-day-ask-link"
          className="py-3 bg-zinc-900 border border-zinc-800 hover:border-violet-600 inline-flex items-center justify-center gap-1.5">
          <MessageCircleQuestion className="w-4 h-4" /> ASK AI
        </Link>
      </div>
      <p className="text-[10px] text-zinc-600 mt-4 text-center">Times come from your saved details — always confirm against airport displays.</p>
    </div>
  );
}
